// =============================================================================
// VandieZ admin-web -- API client
// =============================================================================
// Thin fetch wrapper that:
//   * Resolves the base URL from VITE_API_BASE_URL (Vite injects at build).
//   * Serializes / parses JSON.
//   * Translates non-2xx responses into `ApiError` carrying the
//     RFC 7807 problem-detail body.
//   * Accepts an optional access token for authenticated calls.
//
// No global auth header injection -- callers pass the token explicitly so
// SSR / server-component callers (none today, but possible later) can use
// the same module without depending on a React context for state.
// =============================================================================

import {
  ApiError,
  type OtpRequestPayload,
  type OtpRequestResponse,
  type OtpVerifyPayload,
  type OtpVerifyResponse,
  type ProblemDetail,
  type RefreshResponse,
  type UserRead,
} from "@/types/auth";
import type {
  CancellationPolicy,
  CancellationPolicyCreatePayload,
  CancellationPolicyListResponse,
} from "@/types/cancellation";
import type {
  FareRule,
  FareRuleCreatePayload,
  FareRuleListResponse,
  PricingModel,
  PricingModelListResponse,
} from "@/types/pricing";
import type {
  KycAdminDecisionPayload,
  KycAdminDecisionResponse,
  KycAdminQueueResponse,
  KycAdminUserDetail,
  KycBulkApprovePayload,
  KycBulkApproveResponse,
} from "@/types/kyc";
import type {
  VehicleAdminDecisionPayload,
  VehicleAdminDecisionResponse,
  VehicleAdminDetail,
  VehicleAdminModelAssignPayload,
  VehicleAdminModelAssignResponse,
  VehicleAdminQueueResponse,
  VehicleBulkApprovePayload,
  VehicleBulkApproveResponse,
  VehicleModelCreatePayload,
  VehicleModelRead,
} from "@/types/vehicle";
import type {
  CapacityBandAdmin,
  CapacityBandCreatePayload,
  Location,
  LocationCreatePayload,
  PlatformSetting,
  ReferenceCategory,
  ReferenceItem,
  ReferenceItemCreatePayload,
  Zone,
  ZoneCreatePayload,
  MasterRoute,
  RouteCreatePayload,
  RouteUpdatePayload,
  StatusMasterResponse,
} from "@/types/masterdata";

const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL) ??
  "http://localhost:8000/api/v1";

interface FetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  accessToken?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, accessToken, signal } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials: "omit",
  };
  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    fetchOptions.signal = signal;
  }

  const response = await fetch(`${BASE_URL}${path}`, fetchOptions);

  // 204 No Content (e.g., /auth/logout) -- nothing to parse.
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  // Read body either way; the API returns problem-detail JSON on errors.
  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Non-JSON response. Surface as a problem-detail-shaped error.
      parsed = {
        status: response.status,
        title: response.statusText,
        detail: text.slice(0, 500),
      };
    }
  }

  if (!response.ok) {
    const problem: ProblemDetail =
      parsed !== null &&
      typeof parsed === "object" &&
      "status" in (parsed)
        ? (parsed as ProblemDetail)
        : {
            status: response.status,
            title: response.statusText,
            detail: `Request failed with status ${response.status}`,
          };
    throw new ApiError(problem);
  }

  return parsed as T;
}

// =============================================================================
// Auth endpoint wrappers (POST /auth/otp/request, /verify, /refresh, /logout, /me)
// =============================================================================
export const authApi = {
  requestOtp(payload: OtpRequestPayload, signal?: AbortSignal): Promise<OtpRequestResponse> {
    return request<OtpRequestResponse>("/auth/otp/request", {
      method: "POST",
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  verifyOtp(payload: OtpVerifyPayload, signal?: AbortSignal): Promise<OtpVerifyResponse> {
    return request<OtpVerifyResponse>("/auth/otp/verify", {
      method: "POST",
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  refresh(refreshToken: string, signal?: AbortSignal): Promise<RefreshResponse> {
    return request<RefreshResponse>("/auth/refresh", {
      method: "POST",
      body: { refresh_token: refreshToken },
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  async logout(accessToken: string, refreshToken?: string): Promise<void> {
    await request<undefined>("/auth/logout", {
      method: "POST",
      accessToken,
      body: refreshToken ? { refresh_token: refreshToken } : undefined,
    });
  },

  me(accessToken: string, signal?: AbortSignal): Promise<UserRead> {
    return request<UserRead>("/auth/me", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// Admin KYC endpoints
// =============================================================================
// Source: backend/app/api/v1/admin.py
//   GET  /admin/kyc/queue                -- paginated FIFO queue of users
//   GET  /admin/kyc/{user_id}            -- full user + documents detail
//   POST /admin/kyc/{user_id}/decision   -- approve/reject/resubmit a doc
//
// All require admin role; the backend's CurrentAdmin dependency rejects
// non-admin tokens with 403.
export const kycAdminApi = {
  queue(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<KycAdminQueueResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {search.set("limit", String(params.limit));}
    if (params.offset !== undefined) {search.set("offset", String(params.offset));}
    const qs = search.toString();
    return request<KycAdminQueueResponse>(
      `/admin/kyc/queue${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  userDetail(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<KycAdminUserDetail> {
    return request<KycAdminUserDetail>(`/admin/kyc/${userId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  decide(
    accessToken: string,
    userId: string,
    payload: KycAdminDecisionPayload,
    signal?: AbortSignal,
  ): Promise<KycAdminDecisionResponse> {
    return request<KycAdminDecisionResponse>(`/admin/kyc/${userId}/decision`, {
      method: "POST",
      accessToken,
      body: payload,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  bulkApprove(
    accessToken: string,
    payload: KycBulkApprovePayload,
  ): Promise<KycBulkApproveResponse> {
    return request<KycBulkApproveResponse>("/admin/kyc/bulk-approve", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  // Fetches a document file as a Blob so it can be rendered via URL.createObjectURL.
  // The /kyc/documents/{id}/file endpoint requires auth (admin role passes the policy);
  // returning the raw Blob lets the caller decide how to render (image, iframe, download).
  async documentFile(
    accessToken: string,
    documentId: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };
    const init: RequestInit = {
      method: "GET",
      headers,
      credentials: "omit",
    };
    if (signal !== undefined) {init.signal = signal;}
    const response = await fetch(
      `${BASE_URL}/kyc/documents/${documentId}/file`,
      init,
    );
    if (!response.ok) {
      throw new ApiError({
        status: response.status,
        title: response.statusText,
        detail: `Failed to fetch document ${documentId}`,
      });
    }
    return response.blob();
  },
};

// =============================================================================
// Admin Vehicle endpoints
// =============================================================================
// Source: backend/app/api/v1/admin.py
//   GET  /admin/vehicles                       -- paginated pending queue
//   GET  /admin/vehicles/{vehicle_id}          -- full detail + docs
//   POST /admin/vehicles/{vehicle_id}/decision -- approve / reject
export const vehicleAdminApi = {
  queue(
    accessToken: string,
    params: {
      limit?: number;
      offset?: number;
      status?: string;
      q?: string;
      /** Curation worklist filter: false = no catalog model, true = has one. */
      model_set?: boolean;
    } = {},
    signal?: AbortSignal,
  ): Promise<VehicleAdminQueueResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    if (params.status !== undefined) {
      search.set("status", params.status);
    }
    if (params.q !== undefined && params.q.length > 0) {
      search.set("q", params.q);
    }
    if (params.model_set !== undefined) {
      search.set("model_set", String(params.model_set));
    }
    const qs = search.toString();
    return request<VehicleAdminQueueResponse>(
      `/admin/vehicles${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    vehicleId: string,
    signal?: AbortSignal,
  ): Promise<VehicleAdminDetail> {
    return request<VehicleAdminDetail>(`/admin/vehicles/${vehicleId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  decide(
    accessToken: string,
    vehicleId: string,
    payload: VehicleAdminDecisionPayload,
    signal?: AbortSignal,
  ): Promise<VehicleAdminDecisionResponse> {
    return request<VehicleAdminDecisionResponse>(
      `/admin/vehicles/${vehicleId}/decision`,
      {
        method: "POST",
        accessToken,
        body: payload,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  bulkApprove(
    accessToken: string,
    payload: VehicleBulkApprovePayload,
  ): Promise<VehicleBulkApproveResponse> {
    return request<VehicleBulkApproveResponse>("/admin/vehicles/bulk-approve", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  assignModel(
    accessToken: string,
    vehicleId: string,
    payload: VehicleAdminModelAssignPayload,
    signal?: AbortSignal,
  ): Promise<VehicleAdminModelAssignResponse> {
    return request<VehicleAdminModelAssignResponse>(
      `/admin/vehicles/${vehicleId}`,
      {
        method: "PATCH",
        accessToken,
        body: payload,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Vehicle-model catalog endpoints
// =============================================================================
// Source: backend/app/api/v1/vehicle_models.py
//   GET  /vehicle-models       -- active catalog rows, for pickers
//   POST /vehicle-models       -- promote a free-text make/model (vehicle.decide)
export const vehicleModelsAdminApi = {
  list(
    accessToken: string,
    params: { q?: string } = {},
    signal?: AbortSignal,
  ): Promise<VehicleModelRead[]> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) {
      search.set("q", params.q);
    }
    const qs = search.toString();
    return request<VehicleModelRead[]>(
      `/vehicle-models${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  create(
    accessToken: string,
    payload: VehicleModelCreatePayload,
  ): Promise<VehicleModelRead> {
    return request<VehicleModelRead>("/vehicle-models", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// Admin Ops endpoints
// =============================================================================
// Source: backend/app/api/v1/ops.py
//   GET /admin/ops/overview -- KPI snapshot computed at request time
import type { OpsDailyResponse, OpsOverviewResponse } from "@/types/ops";

export const opsAdminApi = {
  overview(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<OpsOverviewResponse> {
    return request<OpsOverviewResponse>("/admin/ops/overview", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  daily(
    accessToken: string,
    days = 30,
    signal?: AbortSignal,
  ): Promise<OpsDailyResponse> {
    return request<OpsDailyResponse>(
      `/admin/ops/daily?days=${String(days)}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Admin Payout endpoints
// =============================================================================
// Source: backend/app/api/v1/payouts.py
//   GET  /admin/payouts/drivers          -- drivers with positive balance
//   GET  /admin/payouts                  -- paginated payout list
//   POST /admin/payouts                  -- initiate (debits wallet)
//   POST /admin/payouts/{id}/processing  -- mark in-flight
//   POST /admin/payouts/{id}/complete    -- mark completed
//   POST /admin/payouts/{id}/fail        -- mark failed (refunds wallet)
import type {
  AdminBankAccountListResponse,
  AdminDriverWithBalanceResponse,
  AdminInitiatePayoutPayload,
  AdminPayoutListResponse,
  AdminPayoutRead,
  AdminPayoutTransitionPayload,
  PayoutStatus,
} from "@/types/payouts";

export const payoutsAdminApi = {
  candidates(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<AdminDriverWithBalanceResponse> {
    return request<AdminDriverWithBalanceResponse>(
      "/admin/payouts/drivers",
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  driverBankAccounts(
    accessToken: string,
    driverUserId: string,
    signal?: AbortSignal,
  ): Promise<AdminBankAccountListResponse> {
    return request<AdminBankAccountListResponse>(
      `/admin/payouts/drivers/${driverUserId}/bank-accounts`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  list(
    accessToken: string,
    params: {
      status?: PayoutStatus;
      driver_user_id?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<AdminPayoutListResponse> {
    const search = new URLSearchParams();
    if (params.status !== undefined) {
      search.set("status", params.status);
    }
    if (params.driver_user_id !== undefined) {
      search.set("driver_user_id", params.driver_user_id);
    }
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminPayoutListResponse>(
      `/admin/payouts${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  initiate(
    accessToken: string,
    payload: AdminInitiatePayoutPayload,
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>("/admin/payouts", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  markProcessing(
    accessToken: string,
    payoutId: string,
    payload: AdminPayoutTransitionPayload,
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>(
      `/admin/payouts/${payoutId}/processing`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  markComplete(
    accessToken: string,
    payoutId: string,
    payload: AdminPayoutTransitionPayload,
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>(
      `/admin/payouts/${payoutId}/complete`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  markFailed(
    accessToken: string,
    payoutId: string,
    payload: AdminPayoutTransitionPayload,
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>(
      `/admin/payouts/${payoutId}/fail`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  // DR-25 / AD-13 -- the approval gate (migration 0097).
  // `markProcessing` and `markComplete` now REFUSE a pending payout; both
  // require `approved`. Approving is a separate, attributable act, which is
  // the whole point: locking stopped two admins paying the same payout
  // twice, but nothing required anyone to authorise it leaving at all.
  approvePayout(
    accessToken: string,
    payoutId: string,
    payload: { note?: string } = {},
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>(`/admin/payouts/${payoutId}/approve`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  /// Freeze during a dispute. Distinct from `markFailed`, which writes a
  /// refund ledger row -- failing an unattempted payout would make the
  /// ledger state something untrue. `reason` is mandatory server-side and
  /// in the database.
  holdPayout(
    accessToken: string,
    payoutId: string,
    payload: { reason: string },
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>(`/admin/payouts/${payoutId}/hold`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  /// Returns the payout to `pending`, NOT to `approved` -- whatever
  /// justified the freeze invalidates the earlier approval.
  releasePayoutHold(
    accessToken: string,
    payoutId: string,
  ): Promise<AdminPayoutRead> {
    return request<AdminPayoutRead>(
      `/admin/payouts/${payoutId}/release-hold`,
      {
        method: "POST",
        accessToken,
        body: {},
      },
    );
  },
};

// =============================================================================
// Admin Dispute endpoints
// =============================================================================
// Source: backend/app/api/v1/admin_disputes.py
//   GET  /admin/disputes               -- queue (default open+under_review)
//   GET  /admin/disputes/{id}          -- detail (auto-claims open → under_review)
//   POST /admin/disputes/{id}/decision -- resolve / reject + ledger writes
import type {
  AdminDisputeDecisionPayload,
  AdminDisputeQueueResponse,
  DisputeRead,
  DisputeStatus,
} from "@/types/disputes";

export const disputesAdminApi = {
  queue(
    accessToken: string,
    params: {
      status?: DisputeStatus;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<AdminDisputeQueueResponse> {
    const search = new URLSearchParams();
    if (params.status !== undefined) {
      search.set("status", params.status);
    }
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminDisputeQueueResponse>(
      `/admin/disputes${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    disputeId: string,
    signal?: AbortSignal,
  ): Promise<DisputeRead> {
    return request<DisputeRead>(`/admin/disputes/${disputeId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  decide(
    accessToken: string,
    disputeId: string,
    payload: AdminDisputeDecisionPayload,
  ): Promise<DisputeRead> {
    return request<DisputeRead>(
      `/admin/disputes/${disputeId}/decision`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },
};

// =============================================================================
// Admin Support ticket endpoints
// =============================================================================
// Source: backend/app/api/v1/admin_support.py
//   GET  /admin/tickets               -- queue, filterable by category + status + mine
//   GET  /admin/tickets/{id}          -- detail with messages
//   POST /admin/tickets/{id}/messages -- admin reply
//   POST /admin/tickets/{id}/status   -- transition status / priority / assign
import type {
  AdminTicketListResponse,
  AdminTicketStatusPayload,
  TicketCategory,
  TicketMessageRead,
  TicketRead,
  TicketReplyPayload,
  TicketStatus,
  SupportCallbackListResponse,
  SupportCallbackStatus,
  TicketWithMessages,
} from "@/types/support";
import type {
  SmsCostDashboard,
  SmsDispatchLogDetail,
  SmsDispatchLogPage,
  SmsEventConfig,
  SmsEventConfigUpdate,
} from "@/types/sms";
import type {
  AdminSupportTicketListResponse,
  SupportTicketDecidePayload,
  SupportTicketDecideResponse,
} from "@/types/support_tickets";
import type {
  AdminRechargeList,
  AdminWalletTransferList,
} from "@/types/wallet_audit";
import type {
  AdminTripDetail,
  AdminTripLiveListResponse,
  AdminTripTimelineResponse,
} from "@/types/admin_trips";
import type {
  CommissionRuleCreatePayload,
  CommissionRuleDeactivateAck,
  CommissionRuleListResponse,
  CommissionRule,
} from "@/types/commission";

export const supportAdminApi = {
  queue(
    accessToken: string,
    params: {
      category?: TicketCategory;
      status?: TicketStatus;
      mine?: boolean;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<AdminTicketListResponse> {
    const search = new URLSearchParams();
    if (params.category !== undefined) {
      search.set("category", params.category);
    }
    if (params.status !== undefined) {
      search.set("status", params.status);
    }
    if (params.mine === true) {
      search.set("mine", "true");
    }
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminTicketListResponse>(
      `/admin/tickets${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    ticketId: string,
    signal?: AbortSignal,
  ): Promise<TicketWithMessages> {
    return request<TicketWithMessages>(`/admin/tickets/${ticketId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  reply(
    accessToken: string,
    ticketId: string,
    payload: TicketReplyPayload,
  ): Promise<TicketMessageRead> {
    return request<TicketMessageRead>(
      `/admin/tickets/${ticketId}/messages`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  updateStatus(
    accessToken: string,
    ticketId: string,
    payload: AdminTicketStatusPayload,
  ): Promise<TicketRead> {
    return request<TicketRead>(`/admin/tickets/${ticketId}/status`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// Admin SMS config endpoints
// =============================================================================
// Source: backend/app/api/v1/admin_sms.py
//   GET  /admin/sms-config            -- list all event configs
//   PUT  /admin/sms-config/{event_key} -- update a single event config
//   GET  /admin/sms-config/_costs     -- today's cost dashboard + daily cap
//   PUT  /admin/sms-config/_costs     -- update daily cap
//   GET  /admin/sms-config/_log       -- paginated dispatch log
export const smsAdminApi = {
  list(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<SmsEventConfig[]> {
    return request<SmsEventConfig[]>("/admin/sms-config", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  update(
    accessToken: string,
    eventKey: string,
    patch: SmsEventConfigUpdate,
  ): Promise<SmsEventConfig> {
    return request<SmsEventConfig>(`/admin/sms-config/${eventKey}`, {
      method: "PUT",
      accessToken,
      body: patch,
    });
  },

  costs(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<SmsCostDashboard> {
    return request<SmsCostDashboard>("/admin/sms-config/_costs", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  updateCap(
    accessToken: string,
    daily_cap_inr: string,
  ): Promise<SmsCostDashboard> {
    return request<SmsCostDashboard>("/admin/sms-config/_costs", {
      method: "PUT",
      accessToken,
      body: { daily_cap_inr },
    });
  },

  listLog(
    accessToken: string,
    opts: {
      event_key?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<SmsDispatchLogPage> {
    const search = new URLSearchParams();
    if (opts.event_key !== undefined) {
      search.set("event_key", opts.event_key);
    }
    if (opts.status !== undefined) {
      search.set("status", opts.status);
    }
    if (opts.limit !== undefined) {
      search.set("limit", String(opts.limit));
    }
    if (opts.offset !== undefined) {
      search.set("offset", String(opts.offset));
    }
    const qs = search.toString();
    return request<SmsDispatchLogPage>(
      `/admin/sms-config/_log${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  logDetail(
    accessToken: string,
    logId: string,
    signal?: AbortSignal,
  ): Promise<SmsDispatchLogDetail> {
    return request<SmsDispatchLogDetail>(
      `/admin/sms-config/_log/${logId}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Admin support mediation ticket endpoints (Spec 6 Plan D)
// =============================================================================
// Source: backend/app/api/v1/admin_support.py support_router
//   GET  /admin/support/tickets?status=open&limit=50&offset=0
//   POST /admin/support/tickets/{id}/decide   -- approved | taken_over
// Pre-auth callback leads (Spec 10 T4). Separate router on the backend
// and a separate queue here -- these rows have no user id, so they
// cannot live in the ticket list.
export const supportCallbacksAdminApi = {
  list(
    accessToken: string,
    params: { status?: string; page?: number; page_size?: number } = {},
    signal?: AbortSignal,
  ): Promise<SupportCallbackListResponse> {
    const qs = new URLSearchParams();
    if (params.status !== undefined) {qs.set("status", params.status);}
    if (params.page !== undefined) {qs.set("page", String(params.page));}
    if (params.page_size !== undefined) {
      qs.set("page_size", String(params.page_size));
    }
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<SupportCallbackListResponse>(
      `/admin/support-callbacks${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  handle(
    accessToken: string,
    callbackId: string,
  ): Promise<{ id: string; status: SupportCallbackStatus }> {
    return request<{ id: string; status: SupportCallbackStatus }>(
      `/admin/support-callbacks/${callbackId}/handle`,
      { method: "POST", accessToken },
    );
  },
};

export const supportTicketsAdminApi = {
  list(
    accessToken: string,
    params: { status?: string; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminSupportTicketListResponse> {
    const search = new URLSearchParams();
    if (params.status !== undefined) {
      search.set("status", params.status);
    }
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminSupportTicketListResponse>(
      `/admin/support/tickets${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  decide(
    accessToken: string,
    ticketId: string,
    payload: SupportTicketDecidePayload,
    signal?: AbortSignal,
  ): Promise<SupportTicketDecideResponse> {
    return request<SupportTicketDecideResponse>(
      `/admin/support/tickets/${ticketId}/decide`,
      {
        method: "POST",
        accessToken,
        body: payload,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Admin wallet audit endpoints (Spec 8 Plan D)
// =============================================================================
// Source: backend/app/api/v1/admin_wallet.py
//   GET /admin/wallet/transfers?limit=50&offset=0  -- driver-to-driver ledger
//   GET /admin/wallet/recharges?limit=50&offset=0  -- Razorpay recharge ledger
// Read-only compliance audit surface; both require admin role.
export const walletAdminApi = {
  listTransfers(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminWalletTransferList> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminWalletTransferList>(
      `/admin/wallet/transfers${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  listRecharges(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminRechargeList> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminRechargeList>(
      `/admin/wallet/recharges${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Admin live-trip view (ops surface)
// =============================================================================
// Source: backend/app/api/v1/admin_trips.py
//   GET /admin/trips/live?limit=50&offset=0
// Returns every trip in an in-flight status with the lane, driver +
// shipper names, vehicle registration, money, and latest GPS sample
// already joined in.  Single round-trip per page; ordered by the most
// recently updated trip first.
// =============================================================================
// Admin commission-rules endpoints
// =============================================================================
// Source: backend/app/api/v1/admin_commission.py
//   GET    /admin/commission/rules        -- list active rules
//   POST   /admin/commission/rules        -- create a rule (any scope)
//   DELETE /admin/commission/rules/{id}   -- soft-deactivate
//
// Scope precedence is load > user > vehicle_type > global; admins layer
// per-customer overrides on top of the global fallback.
export const cancellationAdminApi = {
  /**
   * The stage x role grid. `includeClosed` adds superseded rows, which
   * is how you answer "what did this cost on the 3rd" -- the question a
   * disputed charge turns into.
   */
  list(
    accessToken: string,
    includeClosed = false,
    signal?: AbortSignal,
  ): Promise<CancellationPolicyListResponse> {
    const qs = includeClosed ? "?include_closed=true" : "";
    return request<CancellationPolicyListResponse>(
      `/admin/cancellation/policies${qs}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  /**
   * Opens a new policy, closing the active one for the same pair in the
   * SAME transaction. There is no update endpoint on purpose: charges
   * are superseded, never overwritten, so past ones stay explainable.
   */
  create(
    accessToken: string,
    payload: CancellationPolicyCreatePayload,
  ): Promise<CancellationPolicy> {
    return request<CancellationPolicy>("/admin/cancellation/policies", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  /**
   * Closes a policy WITHOUT opening a replacement, leaving the pair to
   * fall back to the built-in default. Rarely what you want -- prefer
   * `create`, which supersedes.
   */
  deactivate(accessToken: string, policyId: string): Promise<CancellationPolicy> {
    return request<CancellationPolicy>(
      `/admin/cancellation/policies/${policyId}`,
      { method: "DELETE", accessToken },
    );
  },
};

export const commissionAdminApi = {
  list(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<CommissionRuleListResponse> {
    return request<CommissionRuleListResponse>("/admin/commission/rules", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  create(
    accessToken: string,
    payload: CommissionRuleCreatePayload,
  ): Promise<CommissionRule> {
    return request<CommissionRule>("/admin/commission/rules", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  /**
   * Soft-deactivates a rule.
   *
   * `reason` is MANDATORY (10-300 chars) and recorded in
   * `audit.commission_rule_changes` — this is the action that changes
   * what everyone matching the rule is charged, and until AD-10 it left
   * no actor and no reason at all. The server rejects a missing or
   * too-short reason with 400, so the caller must collect one.
   */
  deactivate(
    accessToken: string,
    ruleId: string,
    reason: string,
  ): Promise<CommissionRuleDeactivateAck> {
    const qs = `?reason=${encodeURIComponent(reason)}`;
    return request<CommissionRuleDeactivateAck>(
      `/admin/commission/rules/${ruleId}${qs}`,
      {
        method: "DELETE",
        accessToken,
      },
    );
  },

};

export const tripsAdminApi = {
  listLive(
    accessToken: string,
    params: {
      limit?: number;
      offset?: number;
      /// Comma-separated list of trip statuses.  Backend defaults to
      /// every in-flight status when omitted.
      status?: string;
    } = {},
    signal?: AbortSignal,
  ): Promise<AdminTripLiveListResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    if (params.status !== undefined && params.status.length > 0) {
      search.set("status", params.status);
    }
    const qs = search.toString();
    return request<AdminTripLiveListResponse>(
      `/admin/trips/live${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    tripId: string,
    signal?: AbortSignal,
  ): Promise<AdminTripDetail> {
    return request<AdminTripDetail>(`/admin/trips/${tripId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  timeline(
    accessToken: string,
    tripId: string,
    signal?: AbortSignal,
  ): Promise<AdminTripTimelineResponse> {
    return request<AdminTripTimelineResponse>(
      `/admin/trips/${tripId}/timeline`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  // ---------------------------------------------------------------------------
  // Trip actions — these hit the existing /trips/{id}/* endpoints (NOT
  // namespaced under /admin/trips/*) because the backend already
  // whitelists admins on the owner-scoped flows. Same endpoint, admin
  // bearer token, primary_role='admin' short-circuits the owner check.
  // ---------------------------------------------------------------------------
  cancel(
    accessToken: string,
    tripId: string,
    payload: { cancellation_reason?: string; repost?: boolean } = {},
  ): Promise<unknown> {
    return request<unknown>(`/trips/${tripId}/cancel`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  replaceVehicle(
    accessToken: string,
    tripId: string,
    payload: {
      new_vehicle_id: string;
      new_driver_id?: string;
      reason?: string;
    },
  ): Promise<unknown> {
    return request<unknown>(`/trips/${tripId}/replacement-vehicle`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  substituteDriver(
    accessToken: string,
    tripId: string,
    payload: { new_driver_id: string; reason?: string },
  ): Promise<unknown> {
    return request<unknown>(`/trips/${tripId}/substitute-driver`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  // Hits the admin-only zero-fee force-cancel (separate from the user-facing
  // /trips/{id}/cancel above, which charges the 5% cancellation fee).
  forceCancel(
    accessToken: string,
    tripId: string,
    reason: string,
  ): Promise<{
    trip_id: string;
    status: string;
    cancellation_fee_inr: string;
    cancelled_at: string;
  }> {
    return request(`/admin/trips/${tripId}/force-cancel`, {
      method: "POST",
      accessToken,
      body: { reason },
    });
  },
};

// =============================================================================
// Admin RBAC endpoints (migration 0042)
// =============================================================================
// Source: backend/app/api/v1/admin_rbac.py + admin_users.py
import type {
  AdminUserDetail,
  AdminUserListResponse,
  BulkSuspendUsersPayload,
  BulkSuspendUsersResponse,
  CreateAdminPayload,
  CreateUserPayload,
  PermissionListResponse,
  Role,
  RoleCreatePayload,
  RoleListResponse,
  RoleUpdatePayload,
  SuspendUserPayload,
  RoleCapabilityMatrix,
  UnsuspendUserPayload,
  UserRoleAssignPayload,
  UserRoleAuditResponse,
  UserRoleListResponse,
  UserTimelineResponse,
} from "@/types/rbac";
import type { AdminUserWalletResponse } from "@/types/admin_user_wallet";
import type { RecentNotificationsResponse } from "@/types/notifications";
import type { AdminUserTripsResponse } from "@/types/admin_user_trips";
import type { AdminUserDevicesResponse } from "@/types/admin_user_devices";
import type {
  AdminUserBankAccountRow,
  AdminUserBankAccountsResponse,
  BankVerificationDecisionPayload,
} from "@/types/admin_user_banks";
import type {
  KycReviewFlagListResponse,
  KycReviewFlagResolvePayload,
  KycReviewFlagRow,
} from "@/types/kyc_review_flags";
import type { AdminUserSmsResponse } from "@/types/admin_user_sms";
import type { AdminUserVehiclesResponse } from "@/types/admin_user_vehicles";
import type { AdminUserDisputesResponse } from "@/types/admin_user_disputes";
import type { AdminLoadDetail, AdminLoadListResponse } from "@/types/admin_load";

export const permissionsAdminApi = {
  list(accessToken: string, signal?: AbortSignal): Promise<PermissionListResponse> {
    return request<PermissionListResponse>("/admin/permissions", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

export const rolesAdminApi = {
  list(accessToken: string, signal?: AbortSignal): Promise<RoleListResponse> {
    return request<RoleListResponse>("/admin/roles", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  create(accessToken: string, payload: RoleCreatePayload): Promise<Role> {
    return request<Role>("/admin/roles", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  update(accessToken: string, roleId: string, payload: RoleUpdatePayload): Promise<Role> {
    return request<Role>(`/admin/roles/${roleId}`, {
      method: "PUT",
      accessToken,
      body: payload,
    });
  },

  async remove(accessToken: string, roleId: string): Promise<void> {
    await request<undefined>(`/admin/roles/${roleId}`, {
      method: "DELETE",
      accessToken,
    });
  },
};

// ---------------------------------------------------------------------------
// Cross-resource admin search (Phase E redesign)
// ---------------------------------------------------------------------------
// Source: backend/app/api/v1/admin_search.py
// Returns up to 5 matches per resource group (users / trips / tickets /
// vehicles). The sidebar's search bar fires this on debounced input.
export interface AdminSearchResult {
  users: Array<{
    id: string;
    name: string | null;
    phone: string | null;
    primary_role: string | null;
  }>;
  trips: Array<{
    id: string;
    lane: string | null;
    status: string;
    driver_name: string | null;
    shipper_name: string | null;
  }>;
  tickets: Array<{
    id: string;
    subject: string;
    status: string;
    user_name: string | null;
  }>;
  vehicles: Array<{
    id: string;
    registration_number: string | null;
    owner_name: string | null;
  }>;
  loads: Array<{
    id: string;
    lane: string | null;
    status: string;
    shipper_name: string | null;
  }>;
}

// ---------------------------------------------------------------------------
// Bid review call recordings (Spec 6)
// ---------------------------------------------------------------------------
// Source: backend/app/api/v1/admin_bid_calls.py
import type {
  AdminBidCallDetail,
  AdminBidCallListResponse,
} from "@/types/bid_calls";

export const bidCallsAdminApi = {
  list(
    accessToken: string,
    params: { status?: string; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminBidCallListResponse> {
    const search = new URLSearchParams();
    if (params.status !== undefined && params.status.length > 0) {
      search.set("status", params.status);
    }
    if (params.limit !== undefined) {
      search.set("limit", String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set("offset", String(params.offset));
    }
    const qs = search.toString();
    return request<AdminBidCallListResponse>(
      `/admin/bid-calls${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    callId: string,
    signal?: AbortSignal,
  ): Promise<AdminBidCallDetail> {
    return request<AdminBidCallDetail>(`/admin/bid-calls/${callId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

export const adminSearchApi = {
  search(
    accessToken: string,
    q: string,
    signal?: AbortSignal,
  ): Promise<AdminSearchResult> {
    return request<AdminSearchResult>(
      `/admin/search?q=${encodeURIComponent(q)}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// usersAdminApi has a `notifications` method that mirrors the inbox
// shape from the user-facing endpoint — same NotificationListResponse
// type as the inbox page; import it here so the admin call inherits
// the same fields.
import type { NotificationListResponse as InboxListResponse } from "@/types/inbox";

// =============================================================================
// Streaming CSV download helper
// =============================================================================
// Backend exposes /admin/exports/*.csv as text/csv streaming responses
// with Content-Disposition: attachment. We can't drop the user into a
// plain <a href=...> because the request needs the access-token
// Authorization header — instead we fetch + blob + simulate a click on
// a synthetic anchor.
//
// Tradeoff: the whole response is buffered in browser memory before the
// download fires. For most exports (10K-100K rows of CSV) that's
// well under 50 MB and fine. For truly enormous datasets we'd need a
// service-worker-backed streaming download, which is out of scope here.
export async function streamCsvDownload(
  accessToken: string,
  endpoint: string,
  filename: string,
): Promise<void> {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: "omit",
  });
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      title: res.statusText,
      detail: `CSV export ${endpoint} failed (${String(res.status)}).`,
    });
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const usersAdminApi = {
  search(
    accessToken: string,
    params: {
      q?: string;
      primary_role?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<AdminUserListResponse> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) {search.set("q", params.q);}
    if (params.primary_role) {search.set("primary_role", params.primary_role);}
    if (params.status) {search.set("status", params.status);}
    if (params.limit !== undefined) {search.set("limit", String(params.limit));}
    if (params.offset !== undefined) {search.set("offset", String(params.offset));}
    const qs = search.toString();
    return request<AdminUserListResponse>(
      `/admin/users${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  detail(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<AdminUserDetail> {
    return request<AdminUserDetail>(`/admin/users/${userId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  suspend(
    accessToken: string,
    userId: string,
    payload: SuspendUserPayload,
  ): Promise<AdminUserDetail> {
    return request<AdminUserDetail>(`/admin/users/${userId}/suspend`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  unsuspend(
    accessToken: string,
    userId: string,
    payload: UnsuspendUserPayload,
  ): Promise<AdminUserDetail> {
    return request<AdminUserDetail>(`/admin/users/${userId}/unsuspend`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  forceLogout(accessToken: string, userId: string): Promise<void> {
    return request<void>(`/admin/users/${userId}/force-logout`, {
      method: "POST",
      accessToken,
    });
  },

  bulkSuspend(
    accessToken: string,
    payload: BulkSuspendUsersPayload,
  ): Promise<BulkSuspendUsersResponse> {
    return request<BulkSuspendUsersResponse>("/admin/users/bulk-suspend", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  listRoles(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<UserRoleListResponse> {
    return request<UserRoleListResponse>(`/admin/users/${userId}/roles`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  replaceRoles(
    accessToken: string,
    userId: string,
    payload: UserRoleAssignPayload,
  ): Promise<UserRoleListResponse> {
    return request<UserRoleListResponse>(`/admin/users/${userId}/roles`, {
      method: "PUT",
      accessToken,
      body: payload,
    });
  },

  listRoleAudit(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<UserRoleAuditResponse> {
    return request<UserRoleAuditResponse>(
      `/admin/users/${userId}/role-audit`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  timeline(
    accessToken: string,
    userId: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<UserTimelineResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<UserTimelineResponse>(
      `/admin/users/${userId}/timeline${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  notifications(
    accessToken: string,
    userId: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<InboxListResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<InboxListResponse>(
      `/admin/users/${userId}/notifications${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  wallet(
    accessToken: string,
    userId: string,
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminUserWalletResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AdminUserWalletResponse>(
      `/admin/users/${userId}/wallet${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  adjustWallet(
    accessToken: string,
    userId: string,
    payload: { direction: "credit" | "debit"; amount: string; note: string },
  ): Promise<{ ledger_entry_id: string; new_balance: string }> {
    return request<{ ledger_entry_id: string; new_balance: string }>(
      `/admin/users/${userId}/wallet/adjust`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  disputes(
    accessToken: string,
    userId: string,
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminUserDisputesResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AdminUserDisputesResponse>(
      `/admin/users/${userId}/disputes${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  vehicles(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<AdminUserVehiclesResponse> {
    return request<AdminUserVehiclesResponse>(
      `/admin/users/${userId}/vehicles`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  smsHistory(
    accessToken: string,
    userId: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminUserSmsResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AdminUserSmsResponse>(
      `/admin/users/${userId}/sms-history${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  bankAccounts(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<AdminUserBankAccountsResponse> {
    return request<AdminUserBankAccountsResponse>(
      `/admin/users/${userId}/bank-accounts`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  devices(
    accessToken: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<AdminUserDevicesResponse> {
    return request<AdminUserDevicesResponse>(
      `/admin/users/${userId}/devices`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  trips(
    accessToken: string,
    userId: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminUserTripsResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AdminUserTripsResponse>(
      `/admin/users/${userId}/trips${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  createAdmin(
    accessToken: string,
    payload: CreateAdminPayload,
  ): Promise<AdminUserDetail> {
    return request<AdminUserDetail>("/admin/users/create-admin", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  // AD-02. A SEPARATE endpoint from createAdmin, not a parameter on it.
  // Creating an admin trips migration 0043's trigger and auto-grants
  // super_admin; the backend guards the two with different permissions for
  // that reason, and collapsing them here would hide the difference from
  // whoever reads this next.
  createUser(
    accessToken: string,
    payload: CreateUserPayload,
  ): Promise<AdminUserDetail> {
    return request<AdminUserDetail>("/admin/users", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// Manual bank-account verification (defect DR-24)
// =============================================================================
// Source: backend/app/api/v1/admin_bank_accounts.py
//   GET  /admin/bank-accounts/pending                                   — queue
//   POST /admin/users/{user_id}/bank-accounts/{account_id}/verification — ruling
//
// Payouts are gated on a verified bank account and there is no automated
// verifier, so the queue below is the only path from "pending" to
// "verified". The DR-24 migration parked every previously auto-verified
// account into `pending`, which is what ops works through.
export const bankAccountsAdminApi = {
  /// The manual-verification work queue, oldest request first.
  pending(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminUserBankAccountsResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AdminUserBankAccountsResponse>(
      `/admin/bank-accounts/pending${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  /// Record the admin's attestation. Returns the updated row so callers can
  /// refresh without a second GET. 409 BANK_ACCOUNT_ALREADY_IN_STATE when
  /// somebody else already ruled on this account.
  verify(
    accessToken: string,
    userId: string,
    accountId: string,
    payload: BankVerificationDecisionPayload,
  ): Promise<AdminUserBankAccountRow> {
    return request<AdminUserBankAccountRow>(
      `/admin/users/${userId}/bank-accounts/${accountId}/verification`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },
};

// =============================================================================
// Admin notifications (test push + broadcast)
// =============================================================================
// Source: backend/app/api/v1/admin_notifications.py
//   POST /admin/notifications/test       — single-user push for FCM debugging
//   POST /admin/notifications/broadcast  — role-filtered fan-out
export interface AdminTestPushPayload {
  user_id: string;
  title: string;
  body: string;
  route_path?: string;
}

export interface AdminTestPushResponse {
  target_user_id: string;
  target_full_name: string;
  success_count: number;
  failure_count: number;
  revoked_token_count: number;
}

export interface AdminBroadcastPayload {
  title: string;
  body: string;
  primary_role?: string;
  kyc_tier?: string;
  route_path?: string;
  preference_key?: string;
}

export interface AdminBroadcastResponse {
  audience_size: number;
  success_count: number;
  failure_count: number;
  revoked_token_count: number;
}

// =============================================================================
// Admin audit log
// =============================================================================
// Source: backend/app/api/v1/admin_audit.py — unified timeline across
// audit.{kyc_review_changes, user_role_changes, user_status_changes}.
import type { AuditActionKind, AuditLogResponse } from "@/types/audit";

// =============================================================================
// Admin system health
// =============================================================================
// Source: backend/app/api/v1/admin_system.py
//   GET /admin/system/health — service-level snapshot
import type {
  DbStatsResponse,
  HealthSummaryResponse,
  MigrationsResponse,
  SystemHealthResponse,
  SystemVersionResponse,
} from "@/types/system";

export const systemAdminApi = {
  health(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<SystemHealthResponse> {
    return request<SystemHealthResponse>("/admin/system/health", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  version(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<SystemVersionResponse> {
    return request<SystemVersionResponse>("/admin/system/version", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  migrations(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<MigrationsResponse> {
    return request<MigrationsResponse>("/admin/system/migrations", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  summary(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<HealthSummaryResponse> {
    return request<HealthSummaryResponse>("/admin/system/summary", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  dbStats(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<DbStatsResponse> {
    return request<DbStatsResponse>("/admin/system/db-stats", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },
};

// =============================================================================
// Admin fraud signals
// =============================================================================
// Source: backend/app/api/v1/admin_fraud.py
//   GET /admin/fraud/signals?days=&limit=  — flagged users + signals
import type { FraudSignalsResponse } from "@/types/fraud";

export const fraudAdminApi = {
  signals(
    accessToken: string,
    params: { days?: number; limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<FraudSignalsResponse> {
    const qs = new URLSearchParams();
    if (params.days !== undefined) {qs.set("days", String(params.days));}
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<FraudSignalsResponse>(
      `/admin/fraud/signals${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// Admin trips at risk
// =============================================================================
// Source: backend/app/api/v1/admin_at_risk.py
//   GET /admin/trips-at-risk?limit=  — flagged trips + heuristics
import type { AtRiskLoadsResponse, AtRiskResponse } from "@/types/at_risk";

export const loadsAdminApi = {
  list(
    accessToken: string,
    params: {
      status?: string;
      pickup_district?: string;
      drop_district?: string;
      shipper_id?: string;
      /** Loads created on or after this date, `YYYY-MM-DD`. */
      created_from?: string;
      /**
       * Loads created on or before this date, `YYYY-MM-DD`.
       *
       * INCLUSIVE of the whole day — the backend filters on the
       * following midnight, so an admin asking for 1-7 August gets
       * everything posted ON the 7th.
       */
      created_to?: string;
      sort?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<AdminLoadListResponse> {
    const qs = new URLSearchParams();
    if (params.status !== undefined) {qs.set("status", params.status);}
    if (params.pickup_district !== undefined) {
      qs.set("pickup_district", params.pickup_district);
    }
    if (params.drop_district !== undefined) {
      qs.set("drop_district", params.drop_district);
    }
    if (params.shipper_id !== undefined) {
      qs.set("shipper_id", params.shipper_id);
    }
    if (params.created_from !== undefined) {
      qs.set("created_from", params.created_from);
    }
    if (params.created_to !== undefined) {
      qs.set("created_to", params.created_to);
    }
    if (params.sort !== undefined) {qs.set("sort", params.sort);}
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AdminLoadListResponse>(`/admin/loads${suffix}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  detail(
    accessToken: string,
    loadId: string,
    signal?: AbortSignal,
  ): Promise<AdminLoadDetail> {
    return request<AdminLoadDetail>(`/admin/loads/${loadId}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  audit(
    accessToken: string,
    loadId: string,
    signal?: AbortSignal,
  ): Promise<{
    entries: Array<{
      changed_at: string;
      actor_user_id: string | null;
      old_status: string | null;
      new_status: string;
      notes: string | null;
      change_source: string | null;
    }>;
  }> {
    return request(`/admin/loads/${loadId}/audit`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  forceExpire(
    accessToken: string,
    loadId: string,
    reason: string,
  ): Promise<{ load_id: string; new_status: string; expired_at: string }> {
    return request<{
      load_id: string;
      new_status: string;
      expired_at: string;
    }>(`/admin/loads/${loadId}/force-expire`, {
      method: "POST",
      accessToken,
      body: { reason },
    });
  },
};

export const atRiskAdminApi = {
  list(
    accessToken: string,
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<AtRiskResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AtRiskResponse>(
      `/admin/trips-at-risk${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  //   GET /admin/loads-at-risk?limit=  — open loads failing to attract
  //   a driver. Its own router on the backend, not a sub-path of
  //   trips-at-risk; hanging it off that prefix once produced
  //   /admin/trips-at-risk/admin/loads-at-risk and nothing noticed.
  listLoads(
    accessToken: string,
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<AtRiskLoadsResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AtRiskLoadsResponse>(
      `/admin/loads-at-risk${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

export const auditAdminApi = {
  search(
    accessToken: string,
    params: {
      actor_user_id?: string;
      target_user_id?: string;
      action_kind?: AuditActionKind;
      since?: string;
      until?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<AuditLogResponse> {
    const search = new URLSearchParams();
    if (params.actor_user_id) {search.set("actor_user_id", params.actor_user_id);}
    if (params.target_user_id) {search.set("target_user_id", params.target_user_id);}
    if (params.action_kind) {search.set("action_kind", params.action_kind);}
    if (params.since) {search.set("since", params.since);}
    if (params.until) {search.set("until", params.until);}
    if (params.limit !== undefined) {search.set("limit", String(params.limit));}
    if (params.offset !== undefined) {search.set("offset", String(params.offset));}
    const qs = search.toString();
    return request<AuditLogResponse>(
      `/admin/audit-log${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },
};

// =============================================================================
// In-app notifications inbox (caller-scoped, not admin-scoped)
// =============================================================================
// Source: backend/app/api/v1/notifications.py
//   GET  /users/me/notifications              — paginated feed + counts
//   POST /users/me/notifications/{id}/read    — flip read_at, idempotent
//   POST /users/me/notifications/read-all     — bulk flip, returns count
import type {
  NotificationListResponse,
  NotificationReadAllResponse,
  NotificationReadResponse,
} from "@/types/inbox";

export const inboxApi = {
  list(
    accessToken: string,
    params: { only_unread?: boolean; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<NotificationListResponse> {
    const qs = new URLSearchParams();
    if (params.only_unread === true) {qs.set("only_unread", "true");}
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<NotificationListResponse>(
      `/users/me/notifications${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  markOneRead(
    accessToken: string,
    notificationId: string,
  ): Promise<NotificationReadResponse> {
    return request<NotificationReadResponse>(
      `/users/me/notifications/${notificationId}/read`,
      {
        method: "POST",
        accessToken,
      },
    );
  },

  markAllRead(
    accessToken: string,
  ): Promise<NotificationReadAllResponse> {
    return request<NotificationReadAllResponse>(
      "/users/me/notifications/read-all",
      {
        method: "POST",
        accessToken,
      },
    );
  },
};

export const notificationsAdminApi = {
  // What was actually sent. The other two methods on this object DISPATCH
  // pushes; nothing here could show an admin the result until 2026-08-11,
  // so "did that broadcast go out?" had no answer in the product.
  recent(
    accessToken: string,
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<RecentNotificationsResponse> {
    const qs =
      params.limit === undefined ? "" : `?limit=${String(params.limit)}`;
    return request<RecentNotificationsResponse>(
      `/admin/notifications/recent${qs}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  test(
    accessToken: string,
    payload: AdminTestPushPayload,
  ): Promise<AdminTestPushResponse> {
    return request<AdminTestPushResponse>("/admin/notifications/test", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  broadcast(
    accessToken: string,
    payload: AdminBroadcastPayload,
  ): Promise<AdminBroadcastResponse> {
    return request<AdminBroadcastResponse>("/admin/notifications/broadcast", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  broadcastPreview(
    accessToken: string,
    payload: { primary_role?: string; kyc_tier?: string },
  ): Promise<{ audience_size: number; sample_user_ids: string[] }> {
    return request<{
      audience_size: number;
      sample_user_ids: string[];
    }>("/admin/notifications/broadcast/preview", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// Admin promotion programs (Spec 7)
// =============================================================================
// Source: backend/app/api/v1/admin_promotions.py
//   GET   /admin/promotions                          -- summary list of all programs
//   GET   /admin/promotions/{code}                   -- single program config
//   PATCH /admin/promotions/{code}                   -- update prize/config/active/effective_to
//   GET   /admin/promotions/{code}/awards            -- per-program award ledger
//   GET   /admin/promotions/awards/pending           -- cross-program pending awards
//   POST  /admin/promotions/{code}/settle            -- dry-run or finalize a period
//   POST  /admin/promotions/awards/{id}/clawback     -- reverse a settled award
// CSV export endpoint (/admin/promotions/{code}/awards/export.csv) is streamed
// via streamCsvDownload above; no client wrapper needed.
import type {
  AwardListResponse,
  PromotionAwardRead,
  PromotionProgramRead,
  PromotionsSummaryResponse,
  SettleResponse,
} from "@/types/promotion";

export const promotionsAdminApi = {
  list(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<PromotionsSummaryResponse> {
    return request<PromotionsSummaryResponse>("/admin/promotions", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  get(
    accessToken: string,
    code: string,
    signal?: AbortSignal,
  ): Promise<PromotionProgramRead> {
    return request<PromotionProgramRead>(`/admin/promotions/${code}`, {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  patch(
    accessToken: string,
    code: string,
    payload: {
      prize_amount?: string;
      config?: Record<string, unknown>;
      is_active?: boolean;
      effective_to?: string | null;
    },
  ): Promise<PromotionProgramRead> {
    return request<PromotionProgramRead>(`/admin/promotions/${code}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  awards(
    accessToken: string,
    code: string,
    params: { status?: string; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AwardListResponse> {
    const qs = new URLSearchParams();
    if (params.status !== undefined) {qs.set("status", params.status);}
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AwardListResponse>(
      `/admin/promotions/${code}/awards${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  pendingAwards(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AwardListResponse> {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<AwardListResponse>(
      `/admin/promotions/awards/pending${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  settle(
    accessToken: string,
    code: string,
    payload: { period: string; dry_run: boolean },
  ): Promise<SettleResponse> {
    return request<SettleResponse>(`/admin/promotions/${code}/settle`, {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  clawback(
    accessToken: string,
    awardId: string,
    reason: string,
  ): Promise<PromotionAwardRead> {
    return request<PromotionAwardRead>(
      `/admin/promotions/awards/${awardId}/clawback`,
      {
        method: "POST",
        accessToken,
        body: { reason },
      },
    );
  },

  // Per-user promotion summary surfaced on UserDetailPage. Joins the
  // user's referral code + referred-by chain + every award they've
  // earned across all programs into a single payload so the admin
  // doesn't have to enumerate programs by hand.
  userPromotions(accessToken: string, userId: string): Promise<{
    awards: Array<{
      id: string;
      program_code: string;
      program_kind: string;
      amount: string;
      status: string;
      awarded_at: string;
      settled_at: string | null;
      trigger_summary: string;
    }>;
    referral_code: string | null;
    referred_by: { id: string; full_name: string } | null;
    referred_count: number;
  }> {
    return request(`/admin/users/${userId}/promotions`, {
      method: "GET", accessToken,
    });
  },
};

// =============================================================================
// Admin vehicle-transfer approvals (VT-08)
// =============================================================================
// Source: backend/app/api/v1/admin_vehicle_transfers.py
//   GET  /admin/vehicle-transfers/pending-approval  -- the queue, OLDEST FIRST
//   POST /admin/vehicle-transfers/{id}/approve      -- mints + returns the code
//   POST /admin/vehicle-transfers/{id}/reject       -- notes are mandatory
//
// Permission: `vehicle.transfer.approve` (migration 0065), distinct from
// `vehicle.transfer` -- "overrule the precondition" vs "start a handover".
//
// The list order is load-bearing and must not be re-sorted client-side: a
// row parked in `admin_approval_required` holds its vehicle's only live
// transfer slot, so queue age is how long a truck has been stuck.
//
// `approve` is the only call in this file whose response contains a
// plaintext secret code. It is returned exactly once (decision D-10) and
// no GET can ever produce it again -- callers must not persist it.
import type {
  AdminParkedTransferListResponse,
  AdminTransferApprovePayload,
  AdminTransferApproveResponse,
  AdminTransferRejectPayload,
  VehicleTransferRead,
} from "@/types/vehicle_transfers";

export const vehicleTransfersAdminApi = {
  pendingApproval(
    accessToken: string,
    params: { limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminParkedTransferListResponse> {
    const search = new URLSearchParams();
    if (params.limit !== undefined) {search.set("limit", String(params.limit));}
    if (params.offset !== undefined) {search.set("offset", String(params.offset));}
    const qs = search.toString();
    return request<AdminParkedTransferListResponse>(
      `/admin/vehicle-transfers/pending-approval${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  approve(
    accessToken: string,
    transferId: string,
    payload: AdminTransferApprovePayload = {},
  ): Promise<AdminTransferApproveResponse> {
    return request<AdminTransferApproveResponse>(
      `/admin/vehicle-transfers/${transferId}/approve`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },

  reject(
    accessToken: string,
    transferId: string,
    payload: AdminTransferRejectPayload,
  ): Promise<VehicleTransferRead> {
    return request<VehicleTransferRead>(
      `/admin/vehicle-transfers/${transferId}/reject`,
      {
        method: "POST",
        accessToken,
        body: payload,
      },
    );
  },
};

// =============================================================================
// Master-data admin endpoints (geographic masters, WS-D/D1)
// =============================================================================
// Source: backend/app/api/v1/masterdata.py
//   GET   /admin/masterdata/locations              -- filterable list
//   POST  /admin/masterdata/locations               -- create (409 LOCATION_DUPLICATE)
//   PATCH /admin/masterdata/locations/{id}           -- partial update
//   GET    /admin/masterdata/zones                        -- list
//   POST   /admin/masterdata/zones                        -- create (409 ZONE_CODE_DUPLICATE, 422 INVALID_ZONE_GEOMETRY)
//   PATCH  /admin/masterdata/zones/{id}                    -- partial update (name / is_active / geometry)
//   POST   /admin/masterdata/zones/{id}/members            -- add a location (409 LOCATION_ALREADY_ZONED, 404 unknown zone/location)
//   DELETE /admin/masterdata/zones/{id}/members/{locId}    -- remove a location (204)
export const masterdataAdminApi = {
  // PR-03 (p122). Not under /admin: the status vocabulary is compiled-in
  // truth every authenticated caller may read, not stored admin data.
  // Read-only by design — see the type docs.
  getStatusMaster(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<StatusMasterResponse> {
    return request<StatusMasterResponse>(
      "/status-master",
      { accessToken, ...(signal !== undefined ? { signal } : {}) },
    );
  },

  // PR-04 (p123). Generated from the app's own routes, so it cannot
  // drift from what is enforced. Read-only by construction — there is
  // nothing to edit here, the gates ARE the matrix.
  getRoleCapabilityMatrix(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<RoleCapabilityMatrix> {
    return request<RoleCapabilityMatrix>(
      "/admin/masterdata/role-capability-matrix",
      { accessToken, ...(signal !== undefined ? { signal } : {}) },
    );
  },

  // C-25 (0109). Used by CommissionRulesPage to suggest real lanes for a
  // route-scoped rule.
  listRoutes(
    accessToken: string,
    params: { active?: boolean; q?: string } = {},
    signal?: AbortSignal,
  ): Promise<MasterRoute[]> {
    const search = new URLSearchParams();
    if (params.active !== undefined) {
      search.set("active", String(params.active));
    }
    if (params.q !== undefined && params.q.length > 0) {
      search.set("q", params.q);
    }
    const qs = search.toString();
    return request<MasterRoute[]>(
      `/admin/masterdata/routes${qs ? `?${qs}` : ""}`,
      { accessToken, ...(signal !== undefined ? { signal } : {}) },
    );
  },

  createRoute(
    accessToken: string,
    payload: RouteCreatePayload,
  ): Promise<MasterRoute> {
    return request<MasterRoute>("/admin/masterdata/routes", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  updateRoute(
    accessToken: string,
    routeId: string,
    payload: RouteUpdatePayload,
  ): Promise<MasterRoute> {
    return request<MasterRoute>(`/admin/masterdata/routes/${routeId}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  listLocations(
    accessToken: string,
    params: {
      state?: string;
      district?: string;
      q?: string;
      active?: boolean;
    } = {},
    signal?: AbortSignal,
  ): Promise<Location[]> {
    const search = new URLSearchParams();
    if (params.state !== undefined && params.state.length > 0) {
      search.set("state", params.state);
    }
    if (params.district !== undefined && params.district.length > 0) {
      search.set("district", params.district);
    }
    if (params.q !== undefined && params.q.length > 0) {
      search.set("q", params.q);
    }
    if (params.active !== undefined) {
      search.set("active", String(params.active));
    }
    const qs = search.toString();
    return request<Location[]>(
      `/admin/masterdata/locations${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  createLocation(
    accessToken: string,
    payload: LocationCreatePayload,
  ): Promise<Location> {
    return request<Location>("/admin/masterdata/locations", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  updateLocation(
    accessToken: string,
    id: string,
    payload: Partial<Location>,
  ): Promise<Location> {
    return request<Location>(`/admin/masterdata/locations/${id}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  listZones(accessToken: string, signal?: AbortSignal): Promise<Zone[]> {
    return request<Zone[]>("/admin/masterdata/zones", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  createZone(
    accessToken: string,
    payload: ZoneCreatePayload,
  ): Promise<Zone> {
    return request<Zone>("/admin/masterdata/zones", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  updateZone(
    accessToken: string,
    id: string,
    payload: { name?: string; is_active?: boolean; geometry?: Record<string, unknown> },
  ): Promise<Zone> {
    return request<Zone>(`/admin/masterdata/zones/${id}`, {
      method: "PATCH",
      accessToken,
      body: payload,
    });
  },

  addZoneMember(
    accessToken: string,
    zoneId: string,
    locationId: string,
  ): Promise<{ zone_id: string; location_id: string }> {
    return request<{ zone_id: string; location_id: string }>(
      `/admin/masterdata/zones/${zoneId}/members`,
      {
        method: "POST",
        accessToken,
        body: { location_id: locationId },
      },
    );
  },

  async removeZoneMember(
    accessToken: string,
    zoneId: string,
    locationId: string,
  ): Promise<void> {
    await request<undefined>(
      `/admin/masterdata/zones/${zoneId}/members/${locationId}`,
      {
        method: "DELETE",
        accessToken,
      },
    );
  },

  // ---------------------------------------------------------------------------
  // Business masters -- reference_data + capacity_bands (AD-16 / C-24)
  // ---------------------------------------------------------------------------
  // These read the ADMIN endpoints, not the public `/reference-data` GET: the
  // public one hides retired rows because a picker should not offer them, but
  // an admin deciding whether to revive a code has to be able to see it.
  //
  // There is no delete method because there is no delete endpoint. Other
  // tables store these codes by value with no foreign key, so removing a row
  // would strand live records silently; retiring is the real operation.
  listReferenceData(
    accessToken: string,
    category: ReferenceCategory,
    signal?: AbortSignal,
  ): Promise<ReferenceItem[]> {
    return request<ReferenceItem[]>(
      `/admin/masterdata/reference-data?category=${category}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  createReferenceItem(
    accessToken: string,
    payload: ReferenceItemCreatePayload,
  ): Promise<ReferenceItem> {
    return request<ReferenceItem>("/admin/masterdata/reference-data", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  updateReferenceItem(
    accessToken: string,
    itemId: string,
    payload: { label?: string; sort_order?: number; is_active?: boolean },
  ): Promise<ReferenceItem> {
    return request<ReferenceItem>(
      `/admin/masterdata/reference-data/${itemId}`,
      {
        method: "PATCH",
        accessToken,
        body: payload,
      },
    );
  },

  // AD-09 / AD-18. There is no create: the key set is fixed by what the
  // backend actually READS, so minting one here would produce a setting
  // nothing consults -- an admin could change it, watch nothing happen, and
  // reasonably conclude the screen is broken.
  listSettings(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<PlatformSetting[]> {
    return request<PlatformSetting[]>("/admin/masterdata/settings", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  updateSetting(
    accessToken: string,
    key: string,
    value: unknown,
  ): Promise<PlatformSetting> {
    return request<PlatformSetting>(
      `/admin/masterdata/settings/${encodeURIComponent(key)}`,
      {
        method: "PATCH",
        accessToken,
        body: { value },
      },
    );
  },

  listCapacityBands(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<CapacityBandAdmin[]> {
    return request<CapacityBandAdmin[]>("/admin/masterdata/capacity-bands", {
      method: "GET",
      accessToken,
      ...(signal !== undefined ? { signal } : {}),
    });
  },

  createCapacityBand(
    accessToken: string,
    payload: CapacityBandCreatePayload,
  ): Promise<CapacityBandAdmin> {
    return request<CapacityBandAdmin>("/admin/masterdata/capacity-bands", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },

  updateCapacityBand(
    accessToken: string,
    bandId: string,
    payload: {
      name?: string;
      min_kg?: number;
      max_kg?: number | null;
      sort_order?: number;
      is_active?: boolean;
    },
  ): Promise<CapacityBandAdmin> {
    return request<CapacityBandAdmin>(
      `/admin/masterdata/capacity-bands/${bandId}`,
      {
        method: "PATCH",
        accessToken,
        body: payload,
      },
    );
  },
};

// =============================================================================
// WS-B pricing configuration (AD-07, AD-08) -- migration 0076
// =============================================================================
// Gated on `masterdata.manage`, the same permission as locations/zones:
// pricing configuration IS master data, and minting a new permission would
// mean a migration plus role grants for no separation anyone asked for.
export const pricingAdminApi = {
  listModels(
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<PricingModelListResponse> {
    return request<PricingModelListResponse>(
      "/admin/masterdata/pricing-models",
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  setModelEnabled(
    accessToken: string,
    code: string,
    isEnabled: boolean,
  ): Promise<PricingModel> {
    return request<PricingModel>(
      `/admin/masterdata/pricing-models/${code}`,
      { method: "PUT", accessToken, body: { is_enabled: isEnabled } },
    );
  },

  /**
   * p9. Moves the recommendation to `code`; the server clears the
   * incumbent in the same transaction. There is no "clear" -- a catalog
   * with no recommendation is the state the field exists to prevent, so
   * recommend a different model instead.
   */
  recommendModel(accessToken: string, code: string): Promise<PricingModel> {
    return request<PricingModel>(
      `/admin/masterdata/pricing-models/${code}`,
      {
        method: "PUT",
        accessToken,
        body: { is_enabled: true, is_recommended: true },
      },
    );
  },

  listFareRules(
    accessToken: string,
    opts: { pricingModel?: string; includeSuperseded?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<FareRuleListResponse> {
    const params = new URLSearchParams();
    if (opts.pricingModel) params.set("pricing_model", opts.pricingModel);
    if (opts.includeSuperseded) params.set("include_superseded", "true");
    const qs = params.toString();
    return request<FareRuleListResponse>(
      `/admin/masterdata/fare-rules${qs ? `?${qs}` : ""}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  createFareRule(
    accessToken: string,
    payload: FareRuleCreatePayload,
  ): Promise<FareRule> {
    return request<FareRule>("/admin/masterdata/fare-rules", {
      method: "POST",
      accessToken,
      body: payload,
    });
  },
};

// =============================================================================
// TR-04 vehicle document override (migration 0070)
// =============================================================================
export interface VehicleDocumentStatus {
  document: string;
  label: string;
  expires_on: string | null;
  state: "valid" | "expiring" | "expired" | "missing";
  days_remaining: number | null;
  /** PUC is monitored but never blocks -- see services/vehicles/compliance.py. */
  blocking: boolean;
}

export interface VehicleCompliance {
  vehicle_id: string;
  registration_number: string;
  is_blocked: boolean;
  documents: VehicleDocumentStatus[];
  /** Stays populated even under an active override, so a client can say
   *  "running on an override" rather than showing a clean bill of health. */
  blocking_documents: string[];
  override_active: boolean;
  override_reason: string | null;
  override_until: string | null;
}

export const vehicleComplianceAdminApi = {
  override(
    accessToken: string,
    vehicleId: string,
    payload: { reason: string; until: string },
  ): Promise<VehicleCompliance> {
    return request<VehicleCompliance>(
      `/admin/vehicles/${vehicleId}/document-override`,
      { method: "POST", accessToken, body: payload },
    );
  },

  revoke(
    accessToken: string,
    vehicleId: string,
  ): Promise<VehicleCompliance> {
    return request<VehicleCompliance>(
      `/admin/vehicles/${vehicleId}/document-override`,
      { method: "DELETE", accessToken },
    );
  },
};

// =============================================================================
// D-20 -- identity-change review flags (migration 0092)
// =============================================================================
// Deliberately separate from the KYC queue: that queue is "nobody has
// ever approved this account", this one is "somebody approved it and
// then the identity moved".
export const kycReviewFlagsAdminApi = {
  /// Open flags, oldest first. Pass openOnly=false to audit past
  /// decisions.
  list(
    accessToken: string,
    params: { openOnly?: boolean; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<KycReviewFlagListResponse> {
    const qs = new URLSearchParams();
    if (params.openOnly !== undefined) {
      qs.set("open_only", String(params.openOnly));
    }
    if (params.limit !== undefined) {qs.set("limit", String(params.limit));}
    if (params.offset !== undefined) {qs.set("offset", String(params.offset));}
    const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
    return request<KycReviewFlagListResponse>(
      `/admin/kyc/review-flags${suffix}`,
      {
        method: "GET",
        accessToken,
        ...(signal !== undefined ? { signal } : {}),
      },
    );
  },

  /// Close a flag. `downgraded` sends the account back to
  /// document_pending and requires a note.
  resolve(
    accessToken: string,
    flagId: string,
    payload: KycReviewFlagResolvePayload,
  ): Promise<KycReviewFlagRow> {
    return request<KycReviewFlagRow>(
      `/admin/kyc/review-flags/${flagId}/resolve`,
      { method: "POST", accessToken, body: payload },
    );
  },
};
